/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: 84 lines
 * - Adding: ~4 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ADMIN_BUTTON_USAGE_WORKING_DATA
 * Date:         2026-01-07
 * Modified By:  Cursor
 *
 * Description:
 * Swaps the working data preview/clean controls onto AdminButton to honor the
 * approved design-system styles for admin actions.
 *
 * File Size Check:
 * - Current: 48
 * - Adding: ~14
 * - Projected: ~62
 * - Decision: Proceed
 *
 * Typography Compliance (UI files only):
 * - Utilities used: .u-P2-16-medium (via btn-figma-1 / btn-danger-figma)
 * - Tokens used: --font-size-body-small, --line-height-body-small, --font-family
 *
 * Related Files:
 * - client/src/components/admin/AdminButton.tsx
 * - client/src/features/admin/scheduler/AdminExchangeRatesPage.tsx
 * - client/src/features/admin/users/AdminWorkingDataBox.tsx
 */

import React from "react";
import { deleteUserWorkingTables } from "../../../services/adminUsersService";
import { notify } from "../../../utils/notifications";
import AdminButton from "../../../components/admin/AdminButton";

type Props = {
  selectedUser: { id: string; name: string; email?: string } | null;
  // [2026-06-26] working-data-gate: id of the user Preview has loaded data for
  previewedUserId?: string | null;
  onOpenPreview: (id: string, name: string, email?: string) => void;
  onCleanCompleted?: () => void;
};

export default function AdminWorkingDataBox({ selectedUser, previewedUserId, onOpenPreview, onCleanCompleted }: Props) {
  // [2026-06-26] working-data-gate: Clean is allowed only after Preview has shown this user's data
  const canClean = !!selectedUser && previewedUserId === selectedUser.id;

  const handlePreview = async () => {
    if (!selectedUser) return;
    onOpenPreview(selectedUser.id, selectedUser.name, selectedUser.email);
  };

  const handleClean = async () => {
    if (!selectedUser || !canClean) return;
    const confirmed = window.confirm(
      `Clean all working/orphan data for ${selectedUser.name}? This is permanent and cannot be undone.`
    );
    if (!confirmed) return;

    try {
      const res = await deleteUserWorkingTables(selectedUser.id);
      const deleted = (res && (res.totalDeleted ?? res.totalDeletedRecords ?? 0)) || 0;
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.success(`Cleaned working data for ${selectedUser.name}. Deleted ${deleted} records`);
      if (onCleanCompleted) onCleanCompleted();
    } catch (err: any) {
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(err, "We couldn't complete the cleanup. Please try again");
    }
  };

  return (
    <div className="border rounded p-3 bg-white" style={{ boxShadow: "none" }}>
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <strong>Working / Orphan Data</strong>
          <div className="text-muted small">Preview and remove orphan or operational working data for a user.</div>
          {/* [2026-06-26] working-data-gate: tell the admin Clean unlocks after Preview */}
          {selectedUser && !canClean && (
            <div className="text-muted small">Press Preview first — Clean unlocks after the data is shown.</div>
          )}
        </div>

        {/* [2026-01-07] Buttons: AdminButton keeps these actions on the approved tokens. */}
        <div className="d-flex gap-2">
          <AdminButton intent="primary" onClick={handlePreview} disabled={!selectedUser}>
            Preview
          </AdminButton>
          {/* [2026-06-26] working-data-gate: Clean disabled until Preview has shown this user's data */}
          <AdminButton intent="danger" onClick={handleClean} disabled={!canClean}>
            Clean
          </AdminButton>
        </div>
      </div>
    </div>
  );
}

